import React, { createContext, useCallback, useRef, useMemo, useContext, useEffect } from 'react';
import Header from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/header';
import { Market } from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/header/types';
import { MarketProvider } from 'context/market';
import { IconsContext } from 'context/icons';
import useResizeObserver from '@loomstateorg/shared-frontend/hooks/useResizeObserver';
import { Category } from 'types/category';
import useSanityPermissionsChecker from 'helpers/hooks/useSanityPermissionsChecker';
import { resolveBrandLogoUrl } from '@loomstateorg/shared-frontend/helpers/brandLogo';
import { LOOM_HEADER_LOOK } from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/header/header-look';

const initialMarketState = {
  market: {} as Market,
  markets: [] as Market[],
  handleMarket: {} as (market: Market) => void,
};

interface Props {
  data: any;
  categories: Category[];
  customerGroup: any;
}

export const MarketContext = createContext(initialMarketState);

const HeaderTastic = ({ data, categories, customerGroup }: Props) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const { setIconsData } = useContext(IconsContext);
  const { checkNestedItemPermission, checkItemPermission } = useSanityPermissionsChecker();

  const setPaddingTop = useCallback(() => {
    if (headerRef.current) {
      document.body.style.paddingTop = `${headerRef.current.clientHeight - 1}px`;
    }
  }, []);

  const removePaddingTop = useCallback(() => {
    document.body.style.paddingTop = '0px';
  }, []);

  // Get cmsData from data (Sanity)
  const cmsData = data?.cmsData;
  const fields = cmsData?.dataSource?.fields;

  const productSearch = !!fields?.productSearch;

  // Filter links based on Sanity permissions
  const filteredData = useMemo(() => {
    const links = fields?.links || [];
    return links.filter((item: any) => {
      const hasPermission = checkNestedItemPermission(item, customerGroup);
      return hasPermission;
    });
  }, [fields?.links, customerGroup, checkNestedItemPermission]);

  // Format Sanity links to the format expected by Header
  const formattedData = useMemo(() => {
    return (filteredData || []).map((link: any) => ({
      linkName: link.linkName || link.name,
      linkUrl: link.linkUrl || link.link,
    }));
  }, [filteredData]);

  const iconsData = useMemo(() => {
    // Get accountIcons from Sanity structure
    const accountIconsDataSource = data?.accountIcons?.dataSource;
    const accountIconsFields = accountIconsDataSource?.fields;

    // Check top-level permissions for accountIcons
    const hasAccountIconsPermission = accountIconsFields
      ? checkNestedItemPermission(accountIconsFields, customerGroup)
      : false;

    if (!hasAccountIconsPermission || !accountIconsFields) {
      return { icons: [], shouldShowIcons: false };
    }

    const shouldShowIcons = accountIconsFields?.shouldShowIcons ?? false;
    const icons = accountIconsFields?.icons ?? [];

    // Filter icons by individual permissions
    const iconsWithPermission = icons.flatMap((icon: any) => {
      const hasPermission = checkNestedItemPermission(icon, customerGroup);
      if (!hasPermission) return {};
      return icon;
    });

    return { icons: iconsWithPermission, shouldShowIcons: !!shouldShowIcons };
  }, [data, customerGroup, checkNestedItemPermission]);

  useEffect(() => {
    setIconsData(iconsData.icons, iconsData.shouldShowIcons);
  }, [iconsData, setIconsData]);

  const logo = useMemo(
    () => resolveBrandLogoUrl(fields, customerGroup, checkItemPermission),
    [fields, customerGroup, checkItemPermission],
  );

  const icon = useMemo(() => {
    const sanityIcon = fields?.icon;
    if (Array.isArray(sanityIcon) && sanityIcon.length > 0) {
      return sanityIcon[0]?.fields?.file?.url || '';
    }
    return '';
  }, [fields?.icon]);

  useResizeObserver(headerRef, setPaddingTop, removePaddingTop);

  return (
    <MarketProvider>
      <div id="header-container" ref={headerRef}>
        <Header
          navLinks={formattedData}
          categories={categories}
          customerGroup={customerGroup}
          logo={logo}
          src={logo}
          icon={icon}
          logoLink={{ type: 'link', link: '/' }}
          logoMobile={data?.logoMobile}
          logoLinkMobile={{ type: 'link', link: '/' }}
          search={productSearch}
          logoScale={fields?.logoScale}
          {...LOOM_HEADER_LOOK}
        />
      </div>
    </MarketProvider>
  );
};

export default HeaderTastic;
